import React, { useState } from "react";
import Select from "react-select";
import {
    getCountryCallingCode,
    getCountries,
    isValidPhoneNumber,
} from "react-phone-number-input";
import ReactCountryFlag from "react-country-flag";
import "react-phone-number-input/style.css";
import { useSignUpContext } from "../../../context/SignUpContext";
import { checkDspAccountExists, sendVerificationOTPOnSignUp } from "../../../apis/verification";
import { GLOBAL_STYLES } from "../../../appConstants";
import { Link } from "react-router-dom";
import Popup from "../../../ReusableFunctions/Popup";

const customStyles = {
    control: (provided) => ({
        ...provided,
        minWidth: "100px",
        maxWidth: "150px",
        height: "35px",
        paddingBottom: "8px",
        fontSize: "14px",
        border: "none",
        boxShadow: "none",
    }),
    menu: (provided) => ({
        ...provided,
        maxHeight: "150px",
        overflowY: "auto",
    }),
    option: (provided) => ({
        ...provided,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
    }),
    singleValue: (provided) => ({
        ...provided,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        margin: 0,  
    }),
    valueContainer: (provided) => ({
    ...provided,
    padding: "0px 0px 0px 12px",         
}),
input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
}),
};

export default function PhoneDetailsStep() {
    const { signUpDetails, setSignUpDetails, phoneMode, setCurrentStep, setPhoneMode } =
        useSignUpContext();
    const [errors, setErrors] = useState({ mobile: "" });
    const [loading, setLoading] = useState(false);

    const contextDialingCode = signUpDetails.countryCode?.replace(/^\+/, "");

    // Country list setup
    const countryOptions = getCountries().map((country) => {
        const callingCode = getCountryCallingCode(country);
        return {
            value: country,
            code: callingCode,
            label: `${country} +${callingCode}`,
            flag: country,
        };
    });


    const defaultCountry =
        countryOptions.find((c) => c.code === contextDialingCode) ||
        countryOptions.find((c) => c.value === "IN") ||
        countryOptions[0];

    const [selectedCountryOption, setSelectedCountryOption] =
        useState(defaultCountry);

    // Update country code
    const handleCountryChange = (option) => {
        setSelectedCountryOption(option);
        const newCode = `+${getCountryCallingCode(option.value)}`;

        setSignUpDetails((prev) => ({
            ...prev,
            countryCode: newCode,
            isMobileVerified: false,
        }));

        validatePhone(signUpDetails.mobile, newCode);
    };

    // Only digits
    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");

        setSignUpDetails((prev) => ({
            ...prev,
            mobile: value,
            isMobileVerified: false,
        }));

        validatePhone(value);
    };

    // PHONE VALIDATION
    const validatePhone = (value, customCode) => {
        const countryCode = customCode || signUpDetails.countryCode;
        let error = "";

        const fullPhone = `${countryCode || ""}${value || ""}`;

        if (!value) error = "Mobile number is required";
        else if (!isValidPhoneNumber(fullPhone)) error = "Invalid number";
        else if (selectedCountryOption.value === "IN" && value.length !== 10)
            error = "Mobile number must be 10 digits";

        setErrors((prev) => ({ ...prev, mobile: error }));
        return error === "" ? false : true;
    };

    // SEND OTP
    const handleSendOtp = async (typeToVerify, valueToSend) => {
        const error = validatePhone(signUpDetails?.mobile);
        setErrors((prev) => ({
            ...prev,
            mobile: error
        }));
        if (error) return;
        if (!error && signUpDetails?.mobile) {
            try {
                setLoading(true);
                await checkDspAccountExists(null, signUpDetails?.mobile);
                if (signUpDetails?.mobile && signUpDetails?.countryCode !== '+91') {
                    setCurrentStep(3);
                } else {
                    await sendVerificationOTPOnSignUp({ [typeToVerify]: valueToSend });
                    setPhoneMode("VERIFY");
                }
            } catch (err) {
                Popup.alert("Error", err || "Something went wrong while sending OTP", "ERROR");
            } finally {
                setLoading(false);
            }
        }
    };
    const isDisabled =
        loading || !signUpDetails.mobile?.trim() || !!errors.mobile;

    return (
        <div className="w-[420px] h-[400px] py-4 mt-2">
            <p className="signUpform-title" style={{
                fontSize: "30px !important",
                fontWeight: 700
            }}>Create Your Account</p>

            <p className="font-manrope font-[600] text-[#1E1E1E] text-[17px] mt-3 mb-4">
                Enter Mobile Number
            </p>

            <div className="form-input-field">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        border: errors.mobile
                            ? "1px solid #D92D20"
                            : "1px solid #D0D5DD",
                        borderRadius: "4px",
                        width: "100%",
                        height: "40px",
                    }}
                >
                    <Select
                        value={selectedCountryOption}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        isSearchable
                        styles={customStyles}
                        formatOptionLabel={(option) => (
                            <div
                                style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                                <ReactCountryFlag
                                    countryCode={option.flag}
                                    svg
                                    style={{ width: "1.2em", height: "1.2em" }}
                                />
                                <span>+{option.code}</span>
                            </div>
                        )}
                    />

                    <div
                        style={{
                            width: "1px",
                            height: "90%",
                            backgroundColor: "#D0D5DD",
                        }}
                    />

                    <input
                        type="text"
                        placeholder="Mobile Number"
                        value={signUpDetails.mobile}
                        onChange={handlePhoneChange}
                        style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            padding: "0 12px",
                            height: "100%",
                            fontSize: "14px",
                        }}
                    />
                </div>

                {errors.mobile && <div className="error-text">{errors.mobile}</div>}
            </div>

            <button
                onClick={() => {
                    handleSendOtp("mobile", signUpDetails?.mobile)
                }}
                disabled={isDisabled}
                className={`w-full text-white rounded-[8px] mt-8 text-[16px] font-[500] h-[40px] ${isDisabled
                    ? "bg-[#0F5EFF] opacity-60 cursor-not-allowed"
                    : "bg-[#0F5EFF] cursor-pointer"
                    }`}
            >
                {signUpDetails?.countryCode !== '+91' ? "Continue" : loading ? "Sending..." : "Send OTP"}
            </button>

            <p className="mt-6 text-center font-manrope font-[500] text-sm text-[#3B3B3B]">
                Already have an account?{" "}
                <Link
                    to={"/login"}
                    style={{
                        color: GLOBAL_STYLES.BG_ACTIVE,
                        textDecoration: "none",
                        fontWeight: 700,
                    }}
                >
                    Log in
                </Link>
            </p>
        </div>
    );
}
